import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Timer, FolderKanban, Receipt, BarChart3, Plus, Users, ChevronDown, Check, DollarSign, CalendarRange, Settings as SettingsIcon, Shield, ShieldAlert, MailWarning, CreditCard, ListChecks, Upload, Building2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";
import UserMenu from "./UserMenu";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/time", label: "Time", icon: Timer },
  { to: "/app/planning", label: "Planning", icon: CalendarRange },
  { to: "/app/clients", label: "Clients", icon: Building2 },
  { to: "/app/projects", label: "Projects", icon: FolderKanban },
  { to: "/app/tasks", label: "Tasks", icon: ListChecks },
  { to: "/app/rate-cards", label: "Rate cards", icon: DollarSign },
  { to: "/app/invoices", label: "Invoices", icon: Receipt },
  { to: "/app/reports", label: "Reports", icon: BarChart3 },
  { to: "/app/members", label: "Team", icon: Users },
  { to: "/app/import", label: "Import", icon: Upload },
  { to: "/app/account", label: "Billing", icon: CreditCard },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

export default function AppShell() {
  const { user, signOut, workspaces, activeWorkspace, switchWorkspace, refreshMe } = useAuth();
  const nav = useNavigate();
  const [wsOpen, setWsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const isStaff = ["readonly", "support", "admin"].includes(user?.platform_role);
  const isPlatformAccess = !!activeWorkspace?.is_platform_access;
  const needsVerification = user && user.email_verified === false;
  const [verifyBusy, setVerifyBusy] = React.useState(false);
  const [verifyMsg, setVerifyMsg] = React.useState("");
  const [alertsCount, setAlertsCount] = React.useState(0);

  React.useEffect(() => {
    if (!activeWorkspace?.id) return;
    let cancelled = false;
    const load = () => {
      api.dashboardNotifications()
        .then((r) => { if (!cancelled) setAlertsCount((r?.alerts || []).length); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [activeWorkspace?.id]);
  const resendVerification = async () => {
    setVerifyBusy(true); setVerifyMsg("");
    try {
      const r = await api.resendVerification();
      if (r.sent) setVerifyMsg("Verification email sent — check your inbox.");
      else if (r.link) setVerifyMsg(`Email service isn't configured yet — open this link to verify: ${r.link}`);
      else setVerifyMsg(r.reason === "already_verified" ? "Already verified — refresh." : "Please try again shortly.");
    } catch {
      setVerifyMsg("Couldn't resend. Try again in a moment.");
    } finally { setVerifyBusy(false); }
  };

  const createWs = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const ws = await api.createWorkspace({ name: newName.trim() });
    await refreshMe();
    switchWorkspace(ws.id);
    setNewName("");
    setCreating(false);
    setWsOpen(false);
  };

  return (
    <div className="min-h-screen bg-cream-2 flex">
      <aside className="w-64 shrink-0 bg-forest text-white flex flex-col">
        <Link to="/app" className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <img className="logo-b" src="/logo-b.png" alt="Full Bench" />
          <span className="font-display text-xl">Full Bench</span>
        </Link>

        {/* Workspace switcher */}
        <div className="px-3 pt-3 relative">
          <button onClick={() => setWsOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm">
            <div className="truncate text-left">
              <div className="text-[10px] uppercase tracking-wide text-white/50">Workspace</div>
              <div className="truncate font-medium">{activeWorkspace?.name || "—"}</div>
            </div>
            <ChevronDown size={14} />
          </button>
          {wsOpen && (
            <div className="absolute z-10 left-3 right-3 mt-1 bg-white text-[#1F2F27] rounded-md shadow-lg border border-cream overflow-hidden">
              {workspaces
                .filter((w) => !w.is_platform_access || w.id === activeWorkspace?.id)
                .map((w) => (
                <button key={w.id} onClick={() => { switchWorkspace(w.id); setWsOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-cream-2 flex items-center justify-between">
                  <span className="truncate">{w.name}</span>
                  {w.id === activeWorkspace?.id && <Check size={14} className="text-coral" />}
                </button>
              ))}
              {isStaff && (
                <button onClick={() => { setWsOpen(false); nav("/staff"); }}
                  data-testid="switcher-open-staff"
                  className="w-full text-left px-3 py-2 text-xs text-coral hover:bg-cream-2 border-t border-cream flex items-center gap-2">
                  <Shield size={12} /> Staff Console (all workspaces)
                </button>
              )}
              <div className="border-t border-cream">
                {creating ? (
                  <form onSubmit={createWs} className="p-2 flex gap-1">
                    <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                      placeholder="Workspace name"
                      className="flex-1 border border-cream rounded px-2 py-1 text-xs" />
                    <button className="btn-coral px-2 rounded text-xs">Add</button>
                  </form>
                ) : (
                  <button onClick={() => setCreating(true)} className="w-full text-left px-3 py-2 text-xs text-coral hover:bg-cream-2">+ New workspace</button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-3 pt-3">
          <Link
            to="/app/time?new=1"
            data-testid="sidebar-new-timer"
            className="btn-coral flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium"
          >
            <Plus size={16} /> New timer
          </Link>
        </div>

        <nav className="px-3 py-4 space-y-1 flex-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                  isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"
                }`
              }>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
          {isStaff && (
            <div className="pt-3 mt-3 border-t border-white/10">
              <div className="px-3 pb-1 text-[10px] uppercase tracking-wide text-white/40">The Bench</div>
              <NavLink to="/staff" end
                data-testid="nav-staff-console"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                    isActive ? "bg-coral/20 text-coral" : "text-coral/80 hover:bg-white/5 hover:text-coral"
                  }`
                }>
                <Shield size={16} /> Staff Console
              </NavLink>
              {user?.platform_role === "admin" && (
                <NavLink to="/staff/team" end
                  data-testid="nav-staff-team"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                      isActive ? "bg-coral/20 text-coral" : "text-coral/80 hover:bg-white/5 hover:text-coral"
                    }`
                  }>
                  <Users size={16} /> Bench Team
                </NavLink>
              )}
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-white/10">
          <UserMenu
            alertsCount={alertsCount}
            onOpenWorkspaceSwitcher={() => setWsOpen(true)}
          />
          <div className="px-2 pt-3 text-[10px] text-white/40">A product by The Bench</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {needsVerification && (
          <div
            data-testid="verify-email-banner"
            className="bg-amber-100 border-b border-amber-300 text-amber-900 px-6 py-2 flex items-center gap-3 text-xs flex-wrap"
          >
            <MailWarning size={14} />
            <span>
              <b>Verify your email</b> — we sent a link to <b>{user?.email}</b>. Some actions may be limited until you confirm.
            </span>
            <button
              onClick={resendVerification}
              disabled={verifyBusy}
              className="ml-auto text-xs bg-amber-900 text-white rounded px-2 py-1 font-medium disabled:opacity-60"
              data-testid="resend-verification-btn"
            >
              {verifyBusy ? "Sending…" : "Resend email"}
            </button>
            {verifyMsg && (
              <div className="basis-full text-[11px] pt-1 text-amber-900/80" data-testid="resend-verification-msg">
                {verifyMsg}
              </div>
            )}
          </div>
        )}
        {isPlatformAccess && (
          <div
            data-testid="bench-staff-banner"
            className="bg-coral/10 border-b border-coral/40 text-forest px-6 py-2 flex items-center gap-2 text-xs"
          >
            <ShieldAlert size={14} className="text-coral" />
            <span className="font-medium">Bench Staff Mode:</span>
            <span className="text-forest/70">
              You're viewing <b>{activeWorkspace?.name}</b> as platform staff — the client cannot see you're here.
            </span>
            <button
              onClick={() => nav("/staff")}
              className="ml-auto text-coral hover:underline font-medium"
              data-testid="banner-exit-staff"
            >
              Exit to Staff Console
            </button>
          </div>
        )}
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
